import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { useInvalidateXpSummary } from './useXpSummary';

/**
 * Refetches the XP summary once each time the app returns to the foreground, so XP earned
 * while backgrounded (e.g. overnight challenge settlement) is reflected on return without
 * waiting for a screen change (specs/xp-live-feedback AC-3). Only fires on a real
 * background→active edge — not on mount, and not on active→active churn. The "+X XP" flash
 * stays suppressed for these grants because useXpGain baselines silently on first load;
 * a background grant surfaces on the ring/level, not as a false "you just earned" flash.
 */
export function useXpForegroundRefresh(): void {
  const invalidateXp = useInvalidateXpSummary();
  const prevStateRef = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      const prev = prevStateRef.current;
      prevStateRef.current = next;
      if (next === 'active' && prev !== 'active') {
        invalidateXp();
      }
    });
    return () => sub.remove();
  }, [invalidateXp]);
}
